import { useEffect, useState, type FormEvent } from 'react';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { api, clearAccountDeviceData } from './api';
import { ErrorNotice } from './components';
import type { User, AccountState } from './AccountJourney';
import { PasskeySettings } from './Passkeys';

function useCountdown(sentAt?: string | null) {
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return sentAt ? Math.max(0, Math.ceil((Date.parse(sentAt) + 60_000 - tick) / 1000)) : 0;
}
function PasswordField({
  id,
  label,
  value,
  onChange,
  newPassword = false,
  reveal = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  newPassword?: boolean;
  reveal?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const field = (
    <input
      id={id}
      name={id}
      type={visible ? 'text' : 'password'}
      autoComplete={newPassword ? 'new-password' : 'current-password'}
      required
      minLength={newPassword ? 10 : 1}
      maxLength={256}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  if (reveal)
    return (
      <div className="account-password-field">
        <label htmlFor={id}>{label}</label>
        <div className="account-password-control">
          {field}
          <button
            type="button"
            aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
            aria-pressed={visible}
            onClick={() => setVisible(!visible)}
          >
            {visible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
    );
  return (
    <>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={id}
        type="password"
        autoComplete={newPassword ? 'new-password' : 'current-password'}
        required
        minLength={newPassword ? 10 : 1}
        maxLength={256}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </>
  );
}
export function VerifyEmail({
  user,
  onChanged,
  onVerified,
  onSignOut,
  onBusyChange,
}: {
  user: User;
  onChanged: (u: User) => void;
  onVerified: (s: AccountState) => void;
  onSignOut?: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [code, setCode] = useState(''),
    [email, setEmail] = useState(user.pendingEmail || user.email),
    [password, setPassword] = useState('');
  const [correcting, setCorrecting] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const seconds = useCountdown(user.verificationSentAt);
  useEffect(() => {
    if (!['pending', 'failed', 'confirming'].includes(user.verificationDelivery || '')) return;
    const timer = setInterval(() => {
      void api<AccountState>('/account/session')
        .then((s) => {
          if (s.user) onChanged(s.user);
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [user.id, user.verificationSentAt, user.verificationDelivery]);
  async function run(action: () => Promise<void>) {
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }
  const target = user.pendingEmail || user.email;
  return (
    <section className="account-form-card account-security-card" aria-labelledby="verify-title">
      <p className="eyebrow">YOUR EMAIL ADDRESS</p>
      <h1 id="verify-title">Verify your email</h1>
      <p>
        Enter the six-digit code sent to <strong className="account-email">{target}</strong>.
      </p>
      <p className="account-hint">
        The code lasts 10 minutes. Use the most recent code and check your junk folder.
      </p>
      {user.verificationDelivery === 'failed' && (
        <ErrorNotice message="We could not send the code email. Please request another code." />
      )}
      {user.verificationDelivery === 'pending' && (
        <p role="status">Your email is queued for sending.</p>
      )}
      {user.verificationDelivery === 'confirming' && (
        <p role="status">We are checking whether your code email was sent. Check your inbox and junk folder.</p>
      )}
      {user.verificationDelivery === 'confirmation_pending' && (
        <p role="status">We could not confirm whether your code email was sent. If it has not arrived, request another code below and use the latest code.</p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () =>
            onVerified(await api<AccountState>('/account/verify-email', 'POST', { code })),
          );
        }}
      >
        <label htmlFor="verification-code">Email verification code</label>
        <input
          id="verification-code"
          className="account-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
        />
        <button className="button primary account-submit" disabled={busy}>
          {busy ? 'Checking…' : 'Verify email'}
        </button>
      </form>
      <button
        className="button secondary"
        disabled={busy || seconds > 0}
        onClick={() =>
          void run(async () => {
            const result = await api<{ user: User; message: string }>(
              '/account/verification-code',
              'POST',
              {},
            );
            onChanged(result.user);
            setNotice(result.message);
          })
        }
      >
        {seconds > 0 ? `Resend code in ${seconds}s` : 'Send another code'}
      </button>
      <button
        className="account-text-button"
        onClick={() => setCorrecting(!correcting)}
        disabled={busy}
      >
        {correcting ? 'Cancel email correction' : 'Wrong email address?'}
      </button>
      {correcting && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const result = await api<{ user: User; message: string }>('/account/email', 'POST', {
                email,
                currentPassword: password,
              });
              onChanged(result.user);
              setPassword('');
              setCorrecting(false);
              setNotice(result.message);
              setCode('');
            });
          }}
        >
          <label htmlFor="correct-email">Correct email address</label>
          <input
            id="correct-email"
            type="email"
            required
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PasswordField
            id="correct-password"
            label="Current password"
            value={password}
            onChange={setPassword}
          />
          <button className="button primary account-submit" disabled={busy}>
            Send code to this address
          </button>
        </form>
      )}
      {notice && (
        <p role="status" className="account-notice">
          {notice}
        </p>
      )}
      {error && <ErrorNotice message={error} />}
      {onSignOut && (
        <button className="account-text-button" onClick={onSignOut} disabled={busy}>
          Sign out
        </button>
      )}
    </section>
  );
}

export function PasswordRecovery({ signInUrl = '/signin' }: { signInUrl?: string }) {
  const [email, setEmail] = useState(''),
    [code, setCode] = useState(''),
    [password, setPassword] = useState(''),
    [confirm, setConfirm] = useState('');
  const [sentAt, setSentAt] = useState<string | null>(null),
    [sent, setSent] = useState(false),
    [done, setDone] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const seconds = useCountdown(sentAt);
  async function requestCode() {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ message: string }>('/account/forgot-password', 'POST', { email });
      setNotice(result.message);
      setSent(true);
      setSentAt(new Date().toISOString());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function reset(e: FormEvent) {
    e.preventDefault();
    const fields = new FormData(e.currentTarget as HTMLFormElement);
    const submittedPassword = String(fields.get('reset-password') ?? '');
    const submittedConfirm = String(fields.get('reset-confirm') ?? '');
    setError('');
    if (submittedPassword !== submittedConfirm) {
      setError('The passwords do not match. Check them and try again.');
      return;
    }
    setBusy(true);
    try {
      await api('/account/reset-password', 'POST', {
        email,
        code: String(fields.get('reset-code') ?? ''),
        password: submittedPassword,
        confirmPassword: submittedConfirm,
      });
      setPassword('');
      setConfirm('');
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="account-form-card account-security-card">
      <p className="eyebrow">ACCOUNT RECOVERY</p>
      <h1>{done ? 'Password reset' : sent ? 'Choose a new password' : 'Forgot your password?'}</h1>
      {done ? (
        <>
          <p>Your password has changed and previous sign-ins have ended.</p>
          <a className="button primary" href={signInUrl}>
            Sign in with your new password
          </a>
        </>
      ) : (
        <>
          <p>
            {sent
              ? `Enter the email code for ${email} and choose a new password.`
              : 'Enter the email address you registered with Marvin.'}
          </p>
          {!sent ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void requestCode();
              }}
            >
              <label htmlFor="recovery-email">Email address</label>
              <input
                id="recovery-email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="button primary account-submit" disabled={busy}>
                {busy ? 'Requesting…' : 'Send reset code'}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={reset}>
                <input name="username" autoComplete="username" value={email} readOnly hidden />
                <label htmlFor="reset-code">Password reset code</label>
                <input
                  id="reset-code"
                  name="reset-code"
                  className="account-code"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                />
                <p className="account-hint">
                  Codes expire after 10 minutes. Requesting another code replaces the previous one.
                </p>
                <PasswordField
                  id="reset-password"
                  label="New password"
                  value={password}
                  onChange={setPassword}
                  newPassword
                />
                <p className="account-hint">
                  Use at least 10 characters. A few unrelated words work well.
                </p>
                <PasswordField
                  id="reset-confirm"
                  label="Confirm new password"
                  value={confirm}
                  onChange={setConfirm}
                  newPassword
                />
                <button className="button primary account-submit" disabled={busy}>
                  {busy ? 'Resetting…' : 'Reset password'}
                </button>
              </form>
              <button
                className="button secondary"
                disabled={busy || seconds > 0}
                onClick={() => void requestCode()}
              >
                {seconds > 0 ? `Resend code in ${seconds}s` : 'Send another reset code'}
              </button>
              <button
                className="account-text-button"
                disabled={busy}
                onClick={() => {
                  setSent(false);
                  setCode('');
                  setNotice('');
                }}
              >
                Use a different email
              </button>
            </>
          )}
          {notice && (
            <p role="status" className="account-notice">
              {notice}
            </p>
          )}
          {error && <ErrorNotice message={error} />}
          <p>
            <a href={signInUrl}>Back to sign in</a>
          </p>
        </>
      )}
    </section>
  );
}

export function AccountSettings({
  user,
  onChanged,
  onVerified,
  onEnded,
  onEditProfile,
  onBack,
  onBusyChange,
  desktop = false,
  onOpenPasskeys,
  onRemoveDevice,
  deviceRemovalBlocked = false,
}: {
  user: User;
  onChanged: (u: User) => void;
  onVerified: (s: AccountState) => void;
  onEnded: (message: string) => void;
  onEditProfile?: () => void;
  onBack?: () => void;
  onBusyChange?: (busy: boolean) => void;
  desktop?: boolean;
  onOpenPasskeys?: () => Promise<void>;
  onRemoveDevice?: () => Promise<void>;
  deviceRemovalBlocked?: boolean;
}) {
  const [email, setEmail] = useState(user.email),
    [emailPassword, setEmailPassword] = useState('');
  const [current, setCurrent] = useState(''),
    [password, setPassword] = useState(''),
    [confirm, setConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState(''),
    [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [panel, setPanel] = useState<'email' | 'password' | null>(null);
  const [errorSection, setErrorSection] = useState('');
  function busyChanged(value: boolean) {
    setBusy(value);
    onBusyChange?.(value);
  }
  function reveal(value: typeof panel) {
    setPanel(value);
    setError('');
    setNotice('');
    setDeleting(false);
    setEmailPassword('');
    setCurrent('');
    setPassword('');
    setConfirm('');
    setDeletePassword('');
    setConfirmation('');
  }
  function cancel(value: 'email' | 'password') {
    reveal(null);
    requestAnimationFrame(() => document.getElementById('open-' + value)?.focus());
  }
  async function run(section: string, action: () => Promise<void>) {
    busyChanged(true);
    setError('');
    setNotice('');
    setErrorSection(section);
    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      busyChanged(false);
    }
  }
  const sectionError = (section: string) =>
    desktop && error && errorSection === section ? <ErrorNotice message={error} /> : null;
  return (
    <div className={desktop ? 'account-settings compact-security' : 'account-settings'}>
      {!desktop && (
        <>
          <p className="eyebrow">YOUR ACCOUNT</p>
          <h1>Profile and account</h1>
          <p className="account-email">
            {user.email} · {user.emailVerified ? 'Email verified' : 'Email not yet verified'}
          </p>
          <section className="account-form-card">
            <h2>Student profile</h2>
            <p>{user.name}</p>
            {onEditProfile ? (
              <button className="button secondary" onClick={onEditProfile}>
                Edit student profile
              </button>
            ) : (
              <a className="button secondary" href="/profile">
                Edit student profile
              </a>
            )}
          </section>
        </>
      )}
      {(user.pendingEmail || !user.emailVerified) && (
        <VerifyEmail
          user={user}
          onChanged={onChanged}
          onVerified={onVerified}
          onBusyChange={busyChanged}
        />
      )}
      {!desktop && user.emailVerified && <PasskeySettings userId={user.id} email={user.email} onBusyChange={busyChanged} />}
      {desktop && onOpenPasskeys && <section className="account-form-card account-security-section"><h2>Passkeys</h2><p>Add, rename or remove the passkeys you use to sign in to Marvin.</p><button disabled={busy} onClick={() => void run('passkeys', onOpenPasskeys)}>Manage passkeys</button>{sectionError('passkeys')}</section>}
      {desktop && onRemoveDevice && <section className="account-form-card account-security-section"><h2>This device</h2><p>Remove this account’s downloaded work and remembered sign-in from this computer. Your server account stays unchanged. You will review and confirm removal in Marvin.</p><button disabled={busy || deviceRemovalBlocked} onClick={() => void run('device', onRemoveDevice)}>Remove account from this device…</button>{deviceRemovalBlocked && <p>Save or cancel your profile changes first.</p>}{sectionError('device')}</section>}
      <section className="account-form-card account-security-section">
        <h2>{desktop ? 'Email address' : 'Change email address'}</h2>
        {desktop && panel !== 'email' ? (
          <div className="account-setting-row">
            <div>
              <div className="account-email-summary">
                <span className="account-email">{user.email}</span>
                <span className="account-verified">
                  {user.emailVerified ? 'Email verified' : 'Email not yet verified'}
                </span>
              </div>
              <p>Used for sign-in and account recovery.</p>
            </div>
            <button
              id="open-email"
              className="button secondary"
              aria-label="Change email address"
              aria-expanded={false}
              disabled={busy}
              onClick={() => reveal('email')}
            >
              Change
            </button>
          </div>
        ) : (
          <div className={desktop ? 'account-inline-form' : undefined}>
            <p>Your sign-in email changes after you verify the code sent to the new address.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void run('email', async () => {
                  const result = await api<{ user: User; message: string }>(
                    '/account/email',
                    'POST',
                    { email, currentPassword: emailPassword },
                  );
                  onChanged(result.user);
                  setEmailPassword('');
                  setNotice(result.message);
                  setPanel(null);
                });
              }}
            >
              <fieldset className="account-fields" disabled={busy}>
                <label htmlFor="new-email">New email address</label>
                <input
                  id="new-email"
                  type="email"
                  required
                  maxLength={254}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <PasswordField
                  id="email-password"
                  label="Current password for email change"
                  value={emailPassword}
                  onChange={setEmailPassword}
                  reveal={desktop}
                />
                {sectionError('email')}
                <div className="account-form-actions">
                  <button className="button primary account-submit" disabled={busy}>
                    {busy ? 'Requesting code…' : 'Verify new email'}
                  </button>
                  {desktop && (
                    <button
                      type="button"
                      className="button secondary quiet"
                      onClick={() => cancel('email')}
                      disabled={busy}
                    >
                      Cancel email change
                    </button>
                  )}
                </div>
              </fieldset>
            </form>
          </div>
        )}
      </section>
      <section className="account-form-card account-security-section">
        <h2>{desktop ? 'Password' : 'Change password'}</h2>
        {desktop && panel !== 'password' ? (
          <div className="account-setting-row">
            <div>
              <span className="account-password-mask" aria-label="Password is set">
                ••••••••••••••••
              </span>
              <p>Choose a unique password for your Marvin account.</p>
            </div>
            <button
              id="open-password"
              className="button secondary"
              aria-label="Change password"
              aria-expanded={false}
              disabled={busy}
              onClick={() => reveal('password')}
            >
              Change
            </button>
          </div>
        ) : (
          <div className={desktop ? 'account-inline-form' : undefined}>
            <p>Changing your password signs you out on all devices.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fields = new FormData(e.currentTarget);
                const submittedPassword = String(fields.get('change-new') ?? '');
                const submittedConfirm = String(fields.get('change-confirm') ?? '');
                if (submittedPassword !== submittedConfirm) {
                  setErrorSection('password');
                  setError('The passwords do not match. Check them and try again.');
                  return;
                }
                void run('password', async () => {
                  await api('/account/password', 'POST', {
                    currentPassword: String(fields.get('change-current') ?? ''),
                    password: submittedPassword,
                    confirmPassword: submittedConfirm,
                  });
                  onEnded('Password changed. Sign in with your new password.');
                });
              }}
            >
              <fieldset className="account-fields" disabled={busy}>
                <input name="username" autoComplete="username" value={user.email} readOnly hidden />
                <PasswordField
                  id="change-current"
                  label="Current password"
                  value={current}
                  onChange={setCurrent}
                  reveal={desktop}
                />
                <PasswordField
                  id="change-new"
                  label="New password"
                  value={password}
                  onChange={setPassword}
                  newPassword
                  reveal={desktop}
                />
                <p className="account-hint">
                  Use at least 10 characters. A few unrelated words work well.
                </p>
                <PasswordField
                  id="change-confirm"
                  label="Confirm new password"
                  value={confirm}
                  onChange={setConfirm}
                  newPassword
                  reveal={desktop}
                />
                {sectionError('password')}
                <div className="account-form-actions">
                  <button className="button primary account-submit" disabled={busy}>
                    {busy ? 'Changing password…' : 'Change password'}
                  </button>
                  {desktop && (
                    <button
                      type="button"
                      className="button secondary quiet"
                      disabled={busy}
                      onClick={() => cancel('password')}
                    >
                      Cancel password change
                    </button>
                  )}
                </div>
              </fieldset>
            </form>
          </div>
        )}
      </section>
      <section className="account-form-card account-danger">
        <h2>Delete account</h2>
        <p>
          This permanently deletes your Marvin account and saved work. Download anything you want to
          keep first.
        </p>
        {(!desktop || deleting) && (
          <p>
            Exported files and copies on other devices are not removed automatically.
            {desktop && ' Marvin will also remove this account’s downloaded data from this device.'}
          </p>
        )}
        {!deleting ? (
          <button
            id="open-deletion"
            className="button secondary"
            disabled={busy}
            onClick={() => {
              if (desktop) reveal(null);
              setDeleting(true);
            }}
          >
            Delete my account
          </button>
        ) : (
          <form
            className={desktop ? 'account-delete-form' : undefined}
            onSubmit={(e) => {
              e.preventDefault();
              void run('delete', async () => {
                await api('/account', 'DELETE', { password: deletePassword, confirmation });
                clearAccountDeviceData(user.id);
                onEnded('Your account and saved Marvin work have been deleted.');
              });
            }}
          >
            <fieldset className="account-fields" disabled={busy}>
              <PasswordField
                id="delete-password"
                label="Password to delete account"
                value={deletePassword}
                onChange={setDeletePassword}
                reveal={desktop}
              />
              <label htmlFor="delete-confirm">Type DELETE to confirm</label>
              <input
                id="delete-confirm"
                autoComplete="off"
                required
                pattern="DELETE"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
              {sectionError('delete')}
              <div className="account-actions">
                <button
                  type="button"
                  className="button secondary"
                  disabled={busy}
                  onClick={() => {
                    setDeleting(false);
                    setDeletePassword('');
                    setConfirmation('');
                    setError('');
                    requestAnimationFrame(() => document.getElementById('open-deletion')?.focus());
                  }}
                >
                  Cancel deletion
                </button>
                <button className="button danger" disabled={busy}>
                  {busy ? 'Deleting…' : 'Permanently delete account'}
                </button>
              </div>
            </fieldset>
          </form>
        )}
      </section>
      {notice && (
        <p role="status" className="account-notice">
          {desktop && <CheckCircle2 size={18} aria-hidden="true" />}
          {notice}
        </p>
      )}
      {!desktop && error && <ErrorNotice message={error} />}
      {!desktop &&
        (onBack ? (
          <button className="account-text-button" onClick={onBack}>
            Back to my workspace
          </button>
        ) : (
          <a href="/home">Back to my home</a>
        ))}
    </div>
  );
}
